import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {ButtonIcon} from '@homeaway/react-buttons';
import {CLOSE__24} from '@homeaway/svg-defs';

import './NotificationBar.less';

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

function NotificationBar({messages, onDismissBar, onDismissMessage, show}) {
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [visible, setVisible] = useState(false);

    const prevCurrentMessageIndex = usePrevious(currentMessageIndex);
    const prevMessagesLength = usePrevious(messages.length);
    const prevShowValue = usePrevious(show);
    const prevVisible = usePrevious(visible);

    useEffect(() => {
        setVisible(show);
    }, [show]);

    useEffect(() => {
        if (prevCurrentMessageIndex < prevMessagesLength && prevShowValue && !prevVisible) {
            setVisible(show);
        }
    });

    function onDismissMessageClick() {
        const nextIdx = currentMessageIndex + 1;
        /* istanbul ignore next */
        if (onDismissMessage) {
            onDismissMessage();
        }

        if (nextIdx >= messages.length) {
            /* istanbul ignore next */
            if (onDismissBar) {
                onDismissBar();
            }
            setVisible(false);
        }

        setCurrentMessageIndex(nextIdx);
    }

    const renderMessages = (message, index) => {
        const id = `NotificationBar__message--${index}`;
        const isCurrent = (index === currentMessageIndex);
        const messageClasses = classNames(
            'NotificationBar__message',
            isCurrent ? 'shown' : 'hidden'
        );

        return (
            <div className={messageClasses} id={id} key={id}>
                {message}
            </div>
        );
    };

    return visible && (
        <div
            className="NotificationBar"
            role="alert"
            aria-live="polite"
        >
            <div className="NotificationBar__content">
                <div className="NotificationBar__messages">
                    {messages.map(renderMessages)}
                </div>
                <ButtonIcon
                    className="NotificationBar__close"
                    label="Close"
                    onClick={onDismissMessageClick}
                    svgMarkup={CLOSE__24}
                    size="xs"
                    inverse
                />
            </div>
        </div>
    );
}

NotificationBar.propTypes = {
    messages: PropTypes.arrayOf(PropTypes.string),
    onDismissBar: PropTypes.func,
    onDismissMessage: PropTypes.func,
    show: PropTypes.bool
};

NotificationBar.defaultProps = {
    messages: [],
    show: false
};

export {NotificationBar};
